import { z } from "zod";
import type { CommentPlacement } from "@common/types/issue";
import { type CardParagraphAnchor, cardParagraphAnchorSchema } from "@common/utils/redline/wire/redline-payload";

const CommentPlacementSchema = z.enum(["first_word", "span", "after_paragraph"]);

const CommentDataSchema = z.object({
  text: z.string().optional(),
  bookmarks: z.array(z.string()).optional(),
  placement: CommentPlacementSchema.nullish(),
  anchor_text: z.string().nullish(),
  paragraph_text: z.string().nullish(),
  paragraph_anchor: cardParagraphAnchorSchema.nullish(),
});

// Canonical tool-error payload (errors.tool_error in the BE). Streamed as
// tool_result content briefly (2026-05-12..14) and still persisted in rows
// from that window.
const ToolErrorSchema = z.object({
  error: z.literal(true),
  message: z.string().optional(),
});

export type CommentToolResult =
  | { kind: "comment"; text: string; bookmarks?: string[]; placement: CommentPlacement | null; anchorText: string | null; paragraphText: string | null; paragraphAnchor: CardParagraphAnchor | null }
  | { kind: "streaming" }
  | { kind: "empty" }
  | { kind: "modelFacingError"; text: string }
  | { kind: "invalid"; error: Error };

// True for tool-result content shapes that are expected to not parse as JSON:
// empty content (errored/interrupted run), plain text (backend
// ToolExecutionError message), or a truncated JSON prefix (still streaming).
// Callers that JSON.parse tool results should skip these silently instead of
// reporting them as parse failures.
export function isKnownNonJsonToolResult(content: string): boolean {
  const trimmed = content.trim();
  return !trimmed || !trimmed.startsWith("{") || !trimmed.endsWith("}");
}

type ToolResultByShape =
  | { parsed: unknown }
  | { kind: "streaming" }
  | { kind: "empty" }
  | { kind: "modelFacingError"; text: string }
  | { kind: "invalid"; error: Error };

function toolResultByShape(content: string, errorFallbackMessage: string): ToolResultByShape {
  const trimmed = content.trim();
  if (!trimmed) return { kind: "empty" };
  if (trimmed.startsWith("{") && !trimmed.endsWith("}")) return { kind: "streaming" };
  if (!trimmed.startsWith("{")) return { kind: "modelFacingError", text: trimmed };
  try {
    const parsed = JSON.parse(trimmed);
    const toolError = ToolErrorSchema.safeParse(parsed);
    if (toolError.success) return { kind: "modelFacingError", text: toolError.data.message ?? errorFallbackMessage };
    return { parsed };
  } catch (error) {
    return { kind: "invalid", error: error instanceof Error ? error : new Error(String(error)) };
  }
}

export function parseCommentToolResult(content: string): CommentToolResult {
  const classified = toolResultByShape(content, "The comment could not be generated.");
  if (!("parsed" in classified)) return classified;
  try {
    const data = CommentDataSchema.parse(classified.parsed);
    return {
      kind: "comment",
      text: data.text ?? "",
      bookmarks: data.bookmarks,
      placement: data.placement ?? null,
      anchorText: data.anchor_text ?? null,
      paragraphText: data.paragraph_text ?? null,
      paragraphAnchor: data.paragraph_anchor ?? null,
    };
  } catch (error) {
    return { kind: "invalid", error: error instanceof Error ? error : new Error(String(error)) };
  }
}

const CommentIdSchema = z
  .union([z.string(), z.number().int().nonnegative()])
  .transform((value) => String(value).trim())
  .pipe(z.string().regex(/^\d+$/));

const ReplyDataSchema = z.object({
  text: z.string().refine((value) => value.trim().length > 0, {
    error: "Reply text must not be blank.",
  }),
  target_comment_id: CommentIdSchema,
  target_comment_text: z.string().nullish(),
});

export type CommentReplyToolResult =
  | { kind: "reply"; text: string; targetCommentId: string; targetCommentText: string | null }
  | { kind: "streaming" }
  | { kind: "empty" }
  | { kind: "modelFacingError"; text: string }
  | { kind: "invalid"; error: Error };

export function parseReplyToolResult(content: string): CommentReplyToolResult {
  const classified = toolResultByShape(content, "The reply could not be generated.");
  if (!("parsed" in classified)) return classified;
  try {
    const data = ReplyDataSchema.parse(classified.parsed);
    return { kind: "reply", text: data.text, targetCommentId: data.target_comment_id, targetCommentText: data.target_comment_text ?? null };
  } catch (error) {
    return { kind: "invalid", error: error instanceof Error ? error : new Error(String(error)) };
  }
}
